<template>
  <div>
    <h1 class="heading-3 mb-12 mt-24 md:mb-24 md:mt-0">
      {{ i18n(translations.mainTitle) }}
    </h1>

    <slot />

    <CartItemCard
      v-for="(item, index) in orderedItems"
      :key="item.listingId"
      class="mb-32 md:mb-40"
      :class="{
        'mt-24': index === 0 && hasPopularityStats(item),
      }"
      :is-first-item-with-unselected-insurance="
        firstItemWithUnselectedInsurance?.listingId === item.listingId
      "
      :item
      :original="getOriginalItem(item)"
      :popularity-stats="getItemPopularityStats(item)"
      :replacements="getItemReplacements(item)"
      :replacements-status="replacementStatus"
      @next="emit('next')"
      @updated="emit('updated')"
      @updating="(item?: CartItem) => emit('updating', item)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, toRef, watch } from 'vue'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { hasInsuranceSelected } from '@backmarket/nuxt-layer-checkout/utils/insurance/hasInsuranceSelected'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { useItemsReplacements } from '~/scopes/checkout/components/Cart/composables/useItemsReplacements'
import { usePopularityStats } from '~/scopes/checkout/components/Cart/composables/usePopularityStats'

import CartItemCard from '../CartItemCard/CartItemCard.vue'

import translations from './CartItems.translations'

const i18n = useI18n()

const props = defineProps<{ items: Array<CartItem> }>()

const emit = defineEmits<{
  updating: [updatedItem?: CartItem]
  updated: []
  next: []
}>()

const { fetchPopularityStats, getItemPopularityStats, hasPopularityStats } =
  usePopularityStats()

const { getOriginalItem, getItemReplacements, replacementStatus } =
  useItemsReplacements(toRef(props, 'items'))

const orderedItems = computed(() => {
  // Unavailable items first
  const items = [...props.items]

  return items.sort((a, b) => {
    if (a.available === 'yes' && b.available === 'no') return 1
    if (b.available === 'yes' && a.available === 'no') return -1

    return 0
  })
})

const firstItemWithUnselectedInsurance = computed(() =>
  props.items.find(
    ({ insuranceOffers }) => !hasInsuranceSelected(insuranceOffers),
  ),
)

watch(
  () => props.items,
  () => {
    fetchPopularityStats(props.items)
  },
  { immediate: true },
)
</script>
